import { useState, useEffect } from 'react'
import { FileText, Cloud, CheckCircle, RefreshCw, Info } from 'lucide-react'
import { toast } from 'sonner'
import { whatsappApi } from '../api/whatsapp'
import { PlantillaCard } from '../components/PlantillaCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import type { Plantilla, PlantillaTwilio } from '../types'

export function PlantillasWhatsApp() {
  const [plantillasLocales, setPlantillasLocales] = useState<Plantilla[]>([])
  const [plantillasTwilio, setPlantillasTwilio] = useState<PlantillaTwilio[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    cargarPlantillas()
  }, [])

  const cargarPlantillas = async () => {
    try {
      const [locales, twilio] = await Promise.all([
        whatsappApi.getPlantillas(),
        whatsappApi.getPlantillasTwilio()
      ])
      setPlantillasLocales(locales)
      setPlantillasTwilio(twilio)
    } catch (error) {
      console.error('Error cargando plantillas:', error)
      toast.error('Error al cargar las plantillas')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = () => {
    setRefreshing(true)
    cargarPlantillas()
  }

  const handleImportar = async (contentSid: string) => {
    try {
      await whatsappApi.importarPlantilla(contentSid)
      toast.success('Plantilla importada correctamente')
      cargarPlantillas()
    } catch (error) {
      console.error('Error importando plantilla:', error)
      toast.error('Error al importar la plantilla')
    }
  }

  const handleEliminar = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar esta plantilla?')) return

    try {
      await whatsappApi.eliminarPlantilla(id)
      toast.success('Plantilla eliminada correctamente')
      cargarPlantillas()
    } catch (error) {
      console.error('Error eliminando plantilla:', error)
      toast.error('Error al eliminar la plantilla')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6 text-black dark:text-white" />
            Plantillas de WhatsApp
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Gestiona tus plantillas aprobadas de Twilio
            </span>
          )}
        </div>
        <Button
          onClick={handleRefresh}
          variant="ghost"
          size="icon"
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel Izquierdo - Twilio */}
        <div>
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="border-b border-gray-300 dark:border-gray-700">
              <CardTitle className="text-base flex items-center gap-2">
                <Cloud className="h-5 w-5 text-teal-500" />
                Disponibles en Twilio
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {plantillasTwilio.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Cloud className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p>No hay plantillas disponibles en Twilio</p>
                </div>
              ) : (
                plantillasTwilio.map((plantilla) => (
                  <PlantillaCard
                    key={plantilla.sid}
                    plantilla={plantilla}
                    type="twilio"
                    onImportar={handleImportar}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel Derecho - Locales */}
        <div>
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="border-b border-gray-300 dark:border-gray-700">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-teal-500" />
                Plantillas Importadas
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {plantillasLocales.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p>No hay plantillas importadas</p>
                  <p className="text-sm mt-1">Importa desde Twilio para comenzar</p>
                </div>
              ) : (
                plantillasLocales.map((plantilla) => (
                  <PlantillaCard
                    key={plantilla.id}
                    plantilla={plantilla}
                    type="local"
                    onEliminar={handleEliminar}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
